"use client";
import { useResource } from "@assistant-ui/tap/react";
import { resource, tapMemo, tapResources, tapEffectEvent, tapInlineResource, tapEffect, tapRef, tapResource, withKey, tapSubscribableResource, } from "@assistant-ui/tap";
import { useAssistantContextValue, DefaultAssistantClient, createRootAssistantClient, } from "./utils/react-assistant-context.js";
import { tapSplitClients, } from "./utils/splitClients.js";
import { normalizeEventSelector, } from "./types/events.js";
import { NotificationManager } from "./utils/NotificationManager.js";
import { withAssistantTapContextProvider } from "./utils/tap-assistant-context.js";
import { tapClientResource } from "./tapClientResource.js";
import { getClientIndex } from "./utils/tap-client-stack-context.js";
import { PROXIED_ASSISTANT_STATE_SYMBOL, createProxiedAssistantState, } from "./utils/proxied-assistant-state.js";
const tapShallowMemoArray = (array) => {
    // biome-ignore lint/correctness/useExhaustiveDependencies: shallow memo
    return tapMemo(() => array, array);
};
const RootClientResource = resource(({ element, emit, clientRef, }) => {
    const { methods, state } = withAssistantTapContextProvider({ clientRef, emit }, () => tapClientResource(element));
    return tapMemo(() => ({ state, methods }), [methods, state]);
});
const RootClientAccessorResource = resource(({ element, notifications, clientRef, name, }) => {
    const store = tapSubscribableResource(RootClientResource({ element, emit: notifications.emit, clientRef }));
    tapEffect(() => {
        return store.subscribe(notifications.notifySubscribers);
    }, [store, notifications]);
    return tapMemo(() => {
        const clientFunction = () => store.getValue().methods;
        Object.defineProperties(clientFunction, {
            source: {
                value: "root",
                writable: false,
            },
            query: {
                value: {},
                writable: false,
            },
            name: {
                value: name,
                configurable: true,
            },
        });
        return clientFunction;
    }, [store, name]);
});
const NoOpRootClientsAccessorsResource = resource(() => {
    return tapMemo(() => ({
        clients: [],
        subscribe: undefined,
        on: undefined,
    }), []);
});
const RootClientsAccessorsResource = resource(({ clients: inputClients, clientRef, }) => {
    const notifications = tapInlineResource(NotificationManager());
    tapEffect(() => clientRef.parent.subscribe(notifications.notifySubscribers), [clientRef, notifications]);
    const results = tapShallowMemoArray(tapResources(() => Object.keys(inputClients).map((key) => withKey(key, RootClientAccessorResource({
        element: inputClients[key],
        notifications,
        clientRef,
        name: key,
    }))), [inputClients, notifications, clientRef]));
    return tapMemo(() => {
        return {
            clients: results,
            subscribe: notifications.subscribe,
            on: function (selector, callback) {
                if (!this) {
                    throw new Error("const { on } = useAui() is not supported. Use aui.on() instead.");
                }
                const { scope, event } = normalizeEventSelector(selector);
                if (scope !== "*") {
                    const source = this[scope].source;
                    if (source === null) {
                        throw new Error(`Scope "${scope}" is not available. Use { scope: "*", event: "${event}" } to listen globally.`);
                    }
                }
                const localUnsub = notifications.on(event, (payload, clientStack) => {
                    if (scope === "*") {
                        callback(payload);
                        return;
                    }
                    const scopeClient = this[scope]();
                    const index = getClientIndex(scopeClient);
                    if (scopeClient === clientStack[index]) {
                        callback(payload);
                    }
                });
                if (scope !== "*" &&
                    clientRef.parent[scope].source === null)
                    return localUnsub;
                const parentUnsub = clientRef.parent.on(selector, callback);
                return () => {
                    localUnsub();
                    parentUnsub();
                };
            },
        };
    }, [results, notifications, clientRef]);
});
const getMeta = (props, clientRef, memo) => {
    if ("source" in props && "query" in props)
        return props;
    if (memo.dep === props)
        return memo.meta;
    const meta = props.getMeta(clientRef.current);
    memo.meta = meta;
    memo.dep = props;
    return meta;
};
const DerivedClientAccessorResource = resource(({ element, clientRef, name, }) => {
    const get = tapEffectEvent(() => element.props);
    return tapMemo(() => {
        const clientFunction = () => get().get(clientRef.current);
        const metaMemo = {};
        Object.defineProperties(clientFunction, {
            source: {
                get: () => getMeta(get(), clientRef, metaMemo).source,
            },
            query: {
                get: () => getMeta(get(), clientRef, metaMemo).query,
            },
            name: {
                value: name,
                configurable: true,
            },
        });
        return clientFunction;
    }, [clientRef, name]);
});
const DerivedClientsAccessorsResource = resource(({ clients, clientRef, }) => {
    return tapShallowMemoArray(tapResources(() => Object.keys(clients).map((key) => withKey(key, DerivedClientAccessorResource({
        element: clients[key],
        clientRef,
        name: key,
    }))), [clients, clientRef]));
});
/**
 * Resource that creates an extended AssistantClient.
 */
export const AssistantClientResource = resource(({ parent, clients, }) => {
    const { rootClients, derivedClients } = tapSplitClients(clients, parent);
    const clientRef = tapRef({
        parent: parent,
        current: null,
    }).current;
    tapEffect(() => {
        // if (clientRef.current && clientRef.current !== client)
        //   throw new Error("clientRef.current !== client");
        clientRef.current = client;
    });
    const rootFields = tapResource(Object.keys(rootClients).length > 0
        ? RootClientsAccessorsResource({ clients: rootClients, clientRef })
        : NoOpRootClientsAccessorsResource());
    const derivedFields = tapInlineResource(DerivedClientsAccessorsResource({ clients: derivedClients, clientRef }));
    const client = tapMemo(() => {
        // Swap DefaultAssistantClient -> createRootAssistantClient at root to change error message
        const proto = parent === DefaultAssistantClient
            ? createRootAssistantClient()
            : parent;
        const client = Object.create(proto);
        Object.assign(client, {
            subscribe: rootFields.subscribe ?? parent.subscribe,
            on: rootFields.on ?? parent.on,
            [PROXIED_ASSISTANT_STATE_SYMBOL]: createProxiedAssistantState(client),
        });
        for (const field of rootFields.clients) {
            client[field.name] = field;
        }
        for (const field of derivedFields) {
            client[field.name] = field;
        }
        return client;
    }, [parent, rootFields, derivedFields]);
    if (clientRef.current === null) {
        clientRef.current = client;
    }
    return client;
});
/** @deprecated This API is highly experimental and may be changed in a minor release */
export function useAui(clients, { parent } = {
    parent: useAssistantContextValue(),
}) {
    if (clients) {
        return useResource(AssistantClientResource({
            parent: parent ?? DefaultAssistantClient,
            clients,
        }));
    }
    if (parent === null)
        throw new Error("received null parent, this usage is not allowed");
    return parent;
}
//# sourceMappingURL=useAui.js.map